import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isClickedOutside, isMobileBreakpoint, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {AbortController | null} */
  #documentAbortController = null;

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {'auto' | 'manual' | null} */
  #previousScrollRestoration = null;

  /** @type {boolean} */
  #isClosing = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartChange);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartChange);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#documentAbortController?.abort();
    this.#setHeaderDrawerState(false);
    this.#restoreHistoryScrollRestoration();
  }

  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      if (dialog.open) return;

      const headerDrawer = document.querySelector('header-drawer');
      const headerDrawerDetails = document.getElementById('Details-menu-drawer-container');
      if (headerDrawerDetails?.hasAttribute('open') && headerDrawer && typeof headerDrawer.close === 'function') {
        headerDrawer.close();
      }

      this.#suspendHistoryScrollRestoration();
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.show();
      this.#setHeaderDrawerState(true);
      this.dispatchEvent(new DialogOpenEvent());

      this.#documentAbortController?.abort();
      this.#documentAbortController = new AbortController();
      const { signal } = this.#documentAbortController;

      document.addEventListener('click', this.#handleDocumentClick, { capture: true, signal });
      document.addEventListener('keydown', this.#handleDocumentKeyDown, { capture: true, signal });
    });
  }

  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open || this.#isClosing) return;

    this.#isClosing = true;
    this.#documentAbortController?.abort();

    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    this.#restorePageScroll();

    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.#setHeaderDrawerState(false);
    this.#isClosing = false;

    this.dispatchEvent(new DialogCloseEvent());
  };

  #restorePageScroll() {
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }

  #restorePageScrollAfterHistoryChange = () => {
    setTimeout(() => {
      requestAnimationFrame(() => {
        this.#restorePageScroll();
        this.#restoreHistoryScrollRestoration();
      });
    }, 0);
  };

  #suspendHistoryScrollRestoration() {
    if (!('scrollRestoration' in history) || this.#previousScrollRestoration !== null) return;

    this.#previousScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';
  }

  #restoreHistoryScrollRestoration() {
    if (!('scrollRestoration' in history) || this.#previousScrollRestoration === null) return;

    history.scrollRestoration = this.#previousScrollRestoration;
    this.#previousScrollRestoration = null;
  }

  #handleDocumentClick = (event) => {
    const { dialog } = this.refs;

    if (!dialog.open || !(event instanceof MouseEvent)) return;

    const target = event.target;
    if (target instanceof Element && target.closest('#Details-menu-drawer-container .header__icon--summary')) return;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  #handleDocumentKeyDown = (event) => {
    if (!(event instanceof KeyboardEvent) || event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  #setHeaderDrawerState(isOpen) {
    const details = document.getElementById('Details-menu-drawer-container');
    if (!(details instanceof HTMLDetailsElement)) return;

    const summary = details.querySelector('.header__icon--summary');
    details.classList.toggle('cart-drawer-open', isOpen);

    if (!(summary instanceof HTMLElement)) return;

    if (isOpen) {
      summary.setAttribute('aria-expanded', 'true');
      summary.setAttribute('aria-label', summary.dataset.cartCloseAriaLabel || summary.getAttribute('aria-label') || '');
      return;
    }

    if (!details.classList.contains('menu-open')) {
      summary.setAttribute('aria-expanded', 'false');
      summary.setAttribute('aria-label', summary.dataset.menuAriaLabel || summary.getAttribute('aria-label') || '');
    }
  }

  #handleDialogOpen = () => {
    this.#syncEmptyState();
    this.#updateStickyState();
    this.#handleHistoryOpen();
  };

  #handleCartChange = () => {
    requestAnimationFrame(() => {
      this.#syncEmptyState();
      this.#updateStickyState();
    });
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      window.addEventListener('popstate', this.#restorePageScrollAfterHistoryChange, { once: true });
      history.back();
      return;
    }

    this.#restoreHistoryScrollRestoration();
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    requestAnimationFrame(() => {
      this.#syncEmptyState();
      this.#updateStickyState();
    });

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  #syncEmptyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const isEmpty = !dialog.querySelector('.cart-form');
    dialog.classList.toggle('cart-drawer--empty', isEmpty);
    dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
